import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IoChevronDown } from "react-icons/io5";
import { splitPriorityNav } from "./priorityNav";

export interface PriorityNavEntry {
  key: string;
  node: React.ReactNode;
}

interface IProps {
  entries: PriorityNavEntry[];
}

const PriorityNaviLinks: React.FC<IProps> = ({ entries }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const moreMeasureRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState<string[] | null>(null);
  const [overflowKeys, setOverflowKeys] = useState<string[]>([]);
  const [moreActive, setMoreActive] = useState(false);

  const entriesKey = entries.map((entry) => entry.key).join("|");

  useLayoutEffect(() => {
    const calculate = () => {
      const container = containerRef.current;
      const measure = measureRef.current;
      if (!container || !measure) return;

      const slots = Array.from(measure.querySelectorAll<HTMLElement>(":scope > .priority-nav__slot"));
      const measuredWidths = slots.map((slot) => slot.offsetWidth);
      if (slots.length > 0 && measuredWidths.every((width) => width === 0)) {
        setVisibleKeys(null);
        setOverflowKeys([]);
        setMoreActive(false);
        return;
      }

      const styles = window.getComputedStyle(measure);
      const gap = parseFloat(styles.columnGap) || 0;
      const moreWidth = moreMeasureRef.current?.offsetWidth ?? 0;
      const split = splitPriorityNav(
        slots.map((slot, index) => ({
          key: slot.getAttribute("data-nav-key") || "",
          width: measuredWidths[index],
          active: Boolean(slot.querySelector(".navi-link.active")),
        })),
        container.clientWidth,
        moreWidth,
        gap,
      );

      setVisibleKeys(split.visibleKeys);
      setOverflowKeys(split.overflowKeys);
      setMoreActive(split.moreActive);
    };

    calculate();

    const resizeObserverCtor = (window as unknown as Record<string, unknown>).ResizeObserver as
      | (new (callback: () => void) => { observe: (target: Element) => void; disconnect: () => void })
      | undefined;

    if (!resizeObserverCtor) {
      window.addEventListener("resize", calculate);
      return () => window.removeEventListener("resize", calculate);
    }

    const observer = new resizeObserverCtor(calculate);
    if (containerRef.current) observer.observe(containerRef.current);
    if (measureRef.current) observer.observe(measureRef.current);

    return () => observer.disconnect();
  }, [entriesKey, location.pathname, location.search, i18n.language]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [open]);

  const visibleKeySet = new Set(visibleKeys ?? entries.map((entry) => entry.key));
  const visibleEntries =
    visibleKeys === null
      ? entries
      : visibleKeys
          .map((key) => entries.find((entry) => entry.key === key))
          .filter((entry): entry is PriorityNavEntry => Boolean(entry));
  const overflowEntries = entries.filter((entry) => overflowKeys.includes(entry.key) && !visibleKeySet.has(entry.key));

  const renderNode = (node: React.ReactNode) => (React.isValidElement(node) ? React.cloneElement(node) : node);

  const renderMoreLabel = () => (
    <>
      {t("wiecej")}
      <IoChevronDown aria-hidden="true" />
    </>
  );

  return (
    <div className="priority-nav" ref={containerRef}>
      <div className="priority-nav__measure-clip">
        <div className="priority-nav__measure" ref={measureRef} aria-hidden="true">
          {entries.map((entry) => (
            <div className="priority-nav__slot" data-nav-key={entry.key} key={entry.key}>
              {renderNode(entry.node)}
            </div>
          ))}
          <button type="button" className="navi-link navi-more__button" ref={moreMeasureRef} tabIndex={-1}>
            {renderMoreLabel()}
          </button>
        </div>
      </div>

      <div className="priority-nav__row">
        {visibleEntries.map((entry) => (
          <div className="priority-nav__slot" key={entry.key}>
            {renderNode(entry.node)}
          </div>
        ))}

        {overflowEntries.length > 0 && (
          <div className="navi-more" ref={menuRef}>
            <button
              type="button"
              className={`navi-link navi-more__button${open ? " is-open" : ""}${moreActive ? " active" : ""}`}
              aria-expanded={open}
              aria-haspopup="true"
              onClick={() => setOpen((current) => !current)}
            >
              {renderMoreLabel()}
            </button>
            {open && (
              <div className="navi-more__menu" role="group" aria-label={t("wiecej")}>
                {overflowEntries.map((entry) => (
                  <div
                    className="navi-more__item"
                    key={entry.key}
                    onClick={(event) => {
                      if ((event.target as HTMLElement).closest("a")) {
                        setOpen(false);
                      }
                    }}
                  >
                    {renderNode(entry.node)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PriorityNaviLinks;
